import { HttpHeaders, HttpClient } from '@angular/common/http';
import { SafeHtml } from '@angular/platform-browser';
import { Injectable, NgZone } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { RuntimeConfigService } from '../../../../services/runtime-config.service';
import { OktaAuthService } from '../../../../services/okta-auth.service';
import { GlobalPollingService } from '../../../../services/global-polling.service';

import { User } from "../../../models/user.model";

@Injectable({
  providedIn: 'root'
})

export class CriticalAlertsService {
  private apiUrl: string;
  private criticalPostsSubject = new BehaviorSubject<CriticalPost[]>([]);
  private lastPostIds: number[] = [];

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private ngZone: NgZone,
    private oktaAuthService: OktaAuthService,
    private globalPollingService: GlobalPollingService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/posts';

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/posts';
    });

    // Subscribe to critical posts updates from global polling service
    this.globalPollingService.getCriticalPosts().subscribe(data => {
      if (data) {
        this.handleCriticalPostsUpdate(data);
      }
    });
  }

  public getCriticalPosts(): Observable<CriticalPost[]> {
    return this.http.get<CriticalPost[]>(`${this.apiUrl}/GetCriticalPosts`);
  }

  public dismissCriticalAlert(postID: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/DismissCriticalAlert/${postID}`);
  }

  public async connectToCriticalPostsPolling(userId: number): Promise<void> {
    // Delegate to global polling service
    return this.globalPollingService.connect(userId);
  }

  private handleCriticalPostsUpdate(data: any): void {
    if (data && data.MessageType === 'critical_posts_update') {
      // Check if post IDs have changed
      const newPostIds = data.PostIds || [];
      const hasChanged = JSON.stringify(this.lastPostIds.sort()) !== JSON.stringify(newPostIds.sort());

      if (hasChanged) {
        this.lastPostIds = newPostIds;

        // Trigger refresh of critical posts
        this.getCriticalPosts().subscribe((posts: CriticalPost[]) => {
          this.ngZone.run(() => {
            this.criticalPostsSubject.next(posts);
          });
        });
      }
    }
  }

  public disconnect(): void {
    // Global service manages the connection, individual services don't disconnect
    // to avoid affecting other subscribers
  }

  public pausePolling(): void {
    this.globalPollingService.pausePolling();
  }

  public resumePolling(): void {
    this.globalPollingService.resumePolling();
  }

  public getCriticalPostsUpdates(): Observable<CriticalPost[]> {
    return this.criticalPostsSubject.asObservable();
  }

  public getConnectionStatus(): Observable<boolean> {
    return this.globalPollingService.getConnectionStatus();
  }

  public isConnected(): boolean {
    return this.globalPollingService.isConnected();
  }

}

export type CriticalPost = {
  Id: number,
  Title: string,
  Description: string,
  PostTypeID: number,
  CreatedDateUTC: Date,
  CreatedByUserID: number,
  PostedDateUTC: Date,
  ScheduledDateUTC: Date,
  ExpiredDateUTC: Date,
  CreatedByUser: User;

  // Helpers
  SanitizedDescription: SafeHtml;
  IsDismissed: boolean;
}
